import { motion, AnimatePresence } from 'framer-motion';
import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Maximize2, X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const SkeletonChart = () => (
  <div className="aspect-square rounded-xl overflow-hidden bg-white p-6">
    <div className="w-full h-full bg-purple-50 rounded-lg flex items-center justify-center">
      <svg className="animate-spin h-16 w-16 text-purple-400" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </div>
  </div>
);

const makeSvgResponsive = (svgString) => {
  if (!svgString) return '';
  return svgString
    .replace(/(<svg[^>]*)\s+width="[^"]*"/gi, '$1')
    .replace(/(<svg[^>]*)\s+height="[^"]*"/gi, '$1')
    .replace(/(<svg[^>]*)>/i, '$1 style="width:100%;height:100%">');
};

const ChartWheel = ({ loading, svgChart }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const transformRef = useRef(null);

  const sanitizedSvg = useMemo(
    () => svgChart ? DOMPurify.sanitize(svgChart, { USE_PROFILES: { svg: true } }) : '',
    [svgChart]
  );

  const responsiveSvg = useMemo(
    () => makeSvgResponsive(sanitizedSvg),
    [sanitizedSvg]
  );

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  useEffect(() => {
    if (!isModalOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') closeModal(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isModalOpen, closeModal]);

  return (
    <>
      <div className="relative flex-1 flex flex-col">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 border border-purple-100 cursor-pointer flex-1"
          onClick={openModal}
        >
          {loading ? (
            <SkeletonChart />
          ) : (
            <>
              <div dangerouslySetInnerHTML={{ __html: sanitizedSvg }} />
              <p className="text-center text-sm text-gray-500 mt-2 flex items-center justify-center gap-1">
                <Maximize2 className="w-4 h-4" />
                Toque para ampliar
              </p>
            </>
          )}
        </motion.div>

        <AnimatePresence>
          {isModalOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 z-50 flex flex-col"
            >
              {/* Header bar */}
              <div className="flex items-center justify-between px-4 py-3 bg-black/40">
                <span className="text-white/70 text-sm">Pinça ou scroll para zoom</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => transformRef.current?.zoomIn(0.5)}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                    aria-label="Zoom in"
                  >
                    <ZoomIn className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => transformRef.current?.zoomOut(0.5)}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                    aria-label="Zoom out"
                  >
                    <ZoomOut className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => transformRef.current?.resetTransform()}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition"
                    aria-label="Resetar zoom"
                  >
                    <RotateCcw className="w-5 h-5" />
                  </button>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition ml-2"
                    aria-label="Fechar"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Zoomable chart area */}
              <div className="flex-1 overflow-hidden">
                <TransformWrapper
                  ref={transformRef}
                  initialScale={1}
                  minScale={0.5}
                  maxScale={8}
                  centerOnInit
                  wheel={{ step: 0.15 }}
                  pinch={{ step: 5 }}
                  doubleClick={{ mode: 'zoomIn', step: 1.5 }}
                >
                  <TransformComponent
                    wrapperStyle={{ width: '100%', height: '100%' }}
                    contentStyle={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <div
                      className="w-full max-w-4xl mx-auto p-4"
                      dangerouslySetInnerHTML={{ __html: responsiveSvg }}
                    />
                  </TransformComponent>
                </TransformWrapper>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default ChartWheel;
